import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Button from '$partials/ui/button';
import Logo from '$partials/icons/logo';
import Image from '$partials/media/image';
import { useForm } from 'react-hook-form';
import {
    SubmitButton,
} from '$partials/forms';
import { useLocale } from '$contexts/locale.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const AwardsModuleNomineesCategory = ({
    copy,
    entrantsCollection,
    googleFormId,
    googleFormFieldName,
    radioButtonVotingEnabled,
    title,
    votingCopy,
    votingHeader
}) => {

    const [errorMessage, setErrorMessage] = useState();
    const [message, setMessage] = useState();
    const [nomineesFormSubmitted, setNomineesFormSubmitted] = useState(false);
    const { translate: T } = useLocale();
    const { handleSubmit } = useForm();

    const [selection, setSelection] = useState();

    const handleChange = (event) => {
        setSelection(event.target.value);
    };

    useEffect(() => {
        if (window.sessionStorage.getItem('nomineesFormSubmitted')) {
            setNomineesFormSubmitted(true);
            setMessage(T('Thank you for your submission!'));
        }
    }, []);

    const onSubmit = async () => {
        try {
            const res = await fetch('/api/v1/google-form', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    formId: googleFormId,
                    fieldName: googleFormFieldName,
                    fieldValue: selection
                })
            });

            if (res.status === 200) {
                setMessage(T('Thank you for your submission!'));
                window.sessionStorage.setItem('nomineesFormSubmitted', true);
                setNomineesFormSubmitted(true);
                setErrorMessage('');
            } else {
                throw new Error(res);
            }
        } catch (err) {
            console.error(err);

            setErrorMessage(T('Sorry, something went wrong. Please try again later.'));
        }
    };

    // render
    return (
        <div className='layout-container'>
            <section className='md:mt-90 mt-60 relative z-10'>
                <div className='px-10 sm:px-20 mb-40'>
                    {title && (
                        <h1 className='type-style-2'>{title}</h1>
                    )}
                    {copy && (
                        <h2 className='sm:w-1/2 mt-30 sm:mt-20 type-style-5'>{copy}</h2>
                    )}
                </div>
                <div className='grid sm:max-md:grid-cols-2 grid-cols-1 gap-20 sm:max-md:px-20 px-10'>
                    {entrantsCollection?.items?.map((entrant, idx) => {
                        return (
                            <div
                                className='bg-peach rounded p-30 md:py-40 md:px-50 flex max-md:flex-col md:-mx-50'
                                key={idx}>
                                <div className='md:w-1/3 flex md:px-20 sm:max-md:mb-30'>
                                    <div className='w-1/2 flex flex-col gap-20 md:gap-25'>
                                        <div className='flex items-center self-start'>
                                            {/* eyebrow logo/text / entrant name */}
                                            {entrant?.eyebrowLogo && (
                                                <div className='mr-5'>
                                                    <img
                                                        alt={entrant.eyebrowLogo.alt}
                                                        className='pointer-events-none'
                                                        height='23'
                                                        loading='lazy'
                                                        role='presentation'
                                                        src={entrant.eyebrowLogo.src}
                                                        width='23' />
                                                </div>
                                            )}

                                            {entrant?.eyebrowText && (
                                                <p className='text-orange type-style-awards-tiny'>{entrant?.eyebrowText}</p>
                                            )}
                                        </div>
                                        {entrant?.entrantName && (
                                            <h3 className='type-style-4'>{entrant?.entrantName}</h3>
                                        )}
                                    </div>
                                    <div className='w-1/2 md:pl-20 hidden md:block'>
                                        {/* Desktop profile photo */}
                                        {entrant?.profilePhoto && (
                                            <div className='aspect-square w-[124px]'>
                                                <Image
                                                    className={'h-full w-full object-cover'}
                                                    containerClassName={'h-full w-full rounded-full'}
                                                    containerStyle={{
                                                        height: '100%',
                                                        paddingBottom: 0,
                                                    }}
                                                    {...entrant?.profilePhoto}/>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='md:w-1/2 md:px-20 flex flex-col gap-30 md:block'>
                                    {/* copy */}
                                    <p className='type-style-8'>
                                        {entrant?.copy}
                                    </p>

                                    {/* Mobile/tablet profile photo */}
                                    <div className='w-1/2 md:pl-20 block md:hidden'>
                                        {entrant?.profilePhoto && (
                                            <div className='aspect-square w-[124px]'>
                                                <Image
                                                    className={'h-full w-full object-cover'}
                                                    containerClassName={'h-full w-full rounded-full'}
                                                    containerStyle={{
                                                        height: '100%',
                                                        paddingBottom: 0,
                                                    }}
                                                    {...entrant?.profilePhoto}/>
                                            </div>
                                        )}
                                    </div>

                                    {/* Link to Strava profile */}
                                    {entrant?.stravaProfileLink && (
                                        <Button
                                            as='a'
                                            className='w-fit flex self-start items-center bg-orange text-white type-style-awards-tiny rounded px-20 py-15 md:mt-40'
                                            href={entrant?.stravaProfileLink}
                                            renderIcon={<Logo />}
                                            target='_blank'>
                                            View profile on
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Radio button voting field */}
            {radioButtonVotingEnabled && (
                <div className='bg-[#F7E6DB] left-1/2 -translate-x-1/2 relative w-screen max-w-screen h-auto sm:min-h-[450px] md:min-h-[640px] -mt-60 sm:-mt-50 md:-mt-125 -z-0'>
                    <div className='layout-container pt-100 sm:pt-90 md:pt-180'>
                        <div className='flex flex-col sm:flex-row gap-60 sm:gap-0'>
                            <h1 className='type-style-1 px-10 sm:px-20 md:w-5/12 sm:w-1/2'>{votingCopy}</h1>
                            {/* spacer */}
                            <div className='hidden md:block md:w-2/12'></div>

                            <div className='flex flex-col type-style-4 sm:w-1/2 md:w-5/12'>
                                {votingHeader && (
                                    <h2 className='px-10 sm:px-20'>{votingHeader}</h2>
                                )}

                                {/* form */}
                                <div className='md:mt-60 mt-35 px-10 sm:px-20'>
                                    {!nomineesFormSubmitted && (
                                        <form
                                            className='flex flex-col gap-35 md:gap-44 '
                                            onSubmit={handleSubmit(onSubmit)}>
                                            {entrantsCollection?.items?.map((entrant, idx) => {
                                                return (
                                                    <div
                                                        className='flex items-center'
                                                        key={idx}>
                                                        <input
                                                            className='mr-[12px] w-full bg-grey border-grey rounded-full placeholder:text-placeholder'
                                                            id={`frm-${title}-${entrant.entrantName}`}
                                                            name='entrant'
                                                            onChange={(event) => handleChange(event)}
                                                            type='radio'
                                                            value={entrant.entrantName}/>
                                                        <label
                                                            className='inline-block border-0 p-0'
                                                            htmlFor={`frm-${title}-${entrant.entrantName}`}>
                                                            {entrant.entrantName}
                                                        </label>
                                                    </div>

                                                );
                                            })}
                                            <SubmitButton className='w-fit bg-orange rounded type-style-awards-tiny text-white px-20 py-15 mb-40 md:mb-80'>
                                                {T('Submit')}
                                            </SubmitButton>
                                        </form>
                                    )}
                                    {errorMessage && (
                                        <p>{errorMessage}</p>
                                    )}
                                    {message && (
                                        <p>{message}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

const propTypes = {
    copy: PropTypes.string,
    entrantsCollection: PropTypes.object,
    googleFormId: PropTypes.string,
    googleFormFieldName: PropTypes.string,
    radioButtonVotingEnabled: PropTypes.bool,
    title: PropTypes.string,
    votingCopy: PropTypes.string,
    votingHeader: PropTypes.string,
};

AwardsModuleNomineesCategory.propTypes = propTypes;

export default AwardsModuleNomineesCategory;
